import * as React from 'react';

import { cn } from '../../lib/utils';
import { controlBase, controlStates } from '../../lib/control';

/**
 * Whether the browser grows a textarea to its text on its own. Chrome and Edge
 * do (`field-sizing: content`); Firefox and Safari do not yet, and get the
 * height set from script instead.
 */
const fieldSizingSupported =
  typeof CSS !== 'undefined' && CSS.supports('field-sizing', 'content');

/**
 * Multi-line input. Carries the same box and the same four states as Input, for
 * the obvious reason: the two sit next to each other on the same form, and a
 * tinted box beside a transparent one reads as a bug rather than as a
 * distinction. Both take them from `lib/control` so that stays true.
 *
 * It grows with what is typed and nothing else: `resize-none` takes away the
 * corner handle, so the box is never dragged taller than its text. Where
 * `field-sizing-content` is supported the browser does the growing; elsewhere
 * the height is matched to the text on every change. `min-h-16` is the floor.
 */
function Textarea({ className, ref, onInput, ...props }: React.ComponentProps<'textarea'>) {
  const inner = React.useRef<HTMLTextAreaElement | null>(null);

  const grow = React.useCallback(() => {
    const box = inner.current;
    if (!box || fieldSizingSupported) return;
    // Back to auto first, so the box can shrink when text is deleted. The
    // border is added because `scrollHeight` leaves it out and the box is
    // `border-box`; without it the last line would sit under a scrollbar.
    box.style.height = 'auto';
    box.style.height = `${box.scrollHeight + box.offsetHeight - box.clientHeight}px`;
  }, []);

  // A value set from outside (a form reset, a restored draft) changes the
  // text without an input event.
  React.useLayoutEffect(grow, [grow, props.value]);

  return (
    <textarea
      data-slot="textarea"
      ref={(node) => {
        inner.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      onInput={(event) => {
        grow();
        onInput?.(event);
      }}
      className={cn(
        controlBase,
        controlStates,
        'field-sizing-content min-h-16 resize-none py-2',
        className,
      )}
      {...props}
    />
  );
}

export { Textarea };
